import { useSyncExternalStore } from "react";

/**
 * Client-side mirror of the server's financial unlock, shared by the API client, the
 * idle guard, the topbar button and the unlock dialog.
 *
 *   open   – a financial section is currently unlocked for this login
 *   locked – the screen was locked (idle or by the user) and shows the cover
 *
 * Other tabs share the same login, and so the same server lock, so lock and unlock are
 * announced through localStorage and every tab follows.
 */
const SYNC_KEY = "aa_financial_lock_sync";

let state = { open: false, locked: false, idleMinutes: 10, reason: "idle" };
const listeners = new Set();

const emit = (next) => {
  state = next;
  listeners.forEach((listener) => listener());
};

const announce = (kind) => {
  try {
    localStorage.setItem(SYNC_KEY, `${kind}:${Date.now()}`);
  } catch {
    // Private mode: the other tabs just catch up on their next request.
  }
};

export const financialLock = {
  get: () => state,
  subscribe: (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** A protected request succeeded, or the password was just confirmed. */
  markOpen(idleMinutes, { broadcast = false } = {}) {
    const minutes = Number(idleMinutes) > 0 ? Number(idleMinutes) : state.idleMinutes;
    emit({ open: true, locked: false, idleMinutes: minutes });
    if (broadcast) announce("unlock");
  },
  setIdleMinutes(minutes) {
    if (Number(minutes) > 0 && Number(minutes) !== state.idleMinutes) emit({ ...state, idleMinutes: Number(minutes) });
  },
  /** reason: "idle" (timed out) or "manual" (the user pressed Bloquear). */
  markLocked({ broadcast = true, reason = "idle" } = {}) {
    if (!state.open && !state.locked) return;
    emit({ ...state, open: false, locked: true, reason });
    if (broadcast) announce(`lock-${reason}`);
  },
  reset() {
    emit({ open: false, locked: false, idleMinutes: state.idleMinutes });
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== SYNC_KEY || !event.newValue) return;
    if (event.newValue.startsWith("lock")) {
      financialLock.markLocked({ broadcast: false, reason: event.newValue.startsWith("lock-manual") ? "manual" : "idle" });
    } else if (event.newValue.startsWith("unlock") && state.locked) {
      financialLock.markOpen(state.idleMinutes);
    }
  });
}

export const useFinancialLock = () => useSyncExternalStore(financialLock.subscribe, financialLock.get);
